import { Component, Input, OnChanges } from '@angular/core';
import type { EChartsOption } from 'echarts';
import { TranslateService } from '@ngx-translate/core';
import { CompanyUtilTimeseriesData } from '../../../interfaces/metrics';

// ISO week number, computed at UTC noon with UTC getters (same algorithm as
// dashboard.component.ts's weekNo) so it agrees with the rest of the dashboard.
function isoWeekNo(iso: string): number {
  const d = new Date(iso + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + 3 - ((d.getUTCDay() + 6) % 7));
  const week1 = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  return 1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getUTCDay() + 6) % 7)) / 7);
}

@Component({
  selector: 'bst-company-util-trend',
  templateUrl: './company-util-trend.component.html',
  styleUrls: ['./company-util-trend.component.scss'],
  standalone: false
})
export class CompanyUtilTrendComponent implements OnChanges {
  @Input() data!: CompanyUtilTimeseriesData;
  options: EChartsOption = {};
  // Two DIFFERENT states, deliberately not merged (the same conflation shipped a bug on
  // capacity-forecast: a fully committed team was told "not enough data").
  //
  // sparse: fewer than 2 weeks carry a reading at all. The resolver emits null for a week
  // it could not measure (no capacity), so null is genuinely absent data.
  // allZero: 2 or more real readings, every one of them 0%. That is not missing data, it is
  // the single most important thing this widget can say, so it gets its own true message.
  // A real 0 must never be filtered out as if it were absent.
  sparse = false;
  allZero = false;
  currentValue: number | null = null;

  constructor(private translate: TranslateService) {}

  ngOnChanges(): void {
    if (!this.data || !this.data.points?.length) { return; }

    this.currentValue = this.data.points[this.data.points.length - 1].utilization;
    const readings = this.data.points.filter((p): p is typeof p & { utilization: number } => p.utilization !== null);
    this.sparse = readings.length < 2;
    this.allZero = !this.sparse && readings.every(p => p.utilization === 0);
    if (this.sparse || this.allZero) { return; }

    const utils = this.data.points.map(p => p.utilization).filter((u): u is number => u !== null);
    const target = this.data.target;
    // Keep the 0-100 (+target) frame so a low line reads AS low, not empty; the
    // gridlines + axis labels below anchor it against that scale.
    const yMax = Math.max(100, target ?? 0, ...utils);
    // ECharts endLabel binds to the last NON-null point, so if the current week
    // has no value it would mislabel the prior week as the line end. Only show it
    // when the current (last) week itself has a value.
    const currentHasValue = this.data.points[this.data.points.length - 1].utilization !== null;
    const targetLabel = this.translate.instant('dashboard.company.trend_target');

    this.options = {
      grid: { left: 38, right: 64, top: 14, bottom: 24 },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: this.data.points.map(p => 'w' + isoWeekNo(p.weekStart)),
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: '#6B7A89', fontFamily: 'inherit', fontWeight: 600 }
      },
      yAxis: {
        type: 'value', min: 0, max: yMax, interval: 50,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: '#6B7A89', fontFamily: 'inherit', fontWeight: 600, formatter: '{value}%' },
        splitLine: { lineStyle: { color: '#EEF1F5' } }
      },
      series: [
        {
          type: 'line',
          smooth: true,
          connectNulls: false,
          data: this.data.points.map(p => p.utilization),
          lineStyle: { color: '#1863DC', width: 2 },
          itemStyle: { color: '#1863DC' },
          symbolSize: 5,
          areaStyle: { color: 'rgba(24, 99, 220, 0.08)' },
          // Label the current-week value at the line end so the latest number pops.
          endLabel: {
            show: currentHasValue, color: '#1863DC', fontFamily: 'inherit', fontWeight: 800,
            formatter: (p: any) => (p.value == null ? '' : p.value + '%')
          },
          markLine: target == null ? undefined : {
            symbol: 'none',
            silent: true,
            lineStyle: { color: '#586876', type: 'dashed', width: 1 },
            label: { position: 'end', formatter: targetLabel + ' ' + target + '%', color: '#586876', fontFamily: 'inherit', fontWeight: 600 },
            data: [{ yAxis: target }]
          }
        }
      ],
      animation: false
    };
  }
}
